import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { BRAND, api, formatApiError } from "@/lib/api";
import { Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { PhotoUploader } from "@/components/PhotoUploader";

const POSITIONS = ["Levantador(a)", "Oposto(a)", "Central", "Ponteiro(a)", "Líbero", "Não definida"];

export default function Register() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    first_name: "", last_name: "", birth_date: "", cpf: "",
    position: "", phone: "", guardian: "", guardian_phone: "",
    email: "", password: "", photo_url: null,
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) return toast.error("Senha mínima de 6 caracteres");
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.position) delete payload.position;
      await api.post("/auth/register", payload);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="register-success">
        <Card className="w-full max-w-xl vv-card p-8 sm:p-12 text-center">
          <div className="flex justify-center mb-6">
            <div className="size-24 sm:size-28 rounded-full bg-[hsl(var(--vv-lime))/0.15] flex items-center justify-center">
              <CheckCircle2 className="size-16 sm:size-20 text-[hsl(var(--vv-lime))]" />
            </div>
          </div>
          <h2 className="text-3xl sm:text-5xl font-black uppercase tracking-wide vv-gradient-text mb-4 leading-tight" data-testid="register-success-title">
            Cadastro<br />Realizado!
          </h2>
          <p className="text-lg sm:text-2xl font-bold text-foreground mb-8" data-testid="register-success-message">
            Aguarde a aprovação do administrador.
          </p>
          <Button onClick={() => nav("/login")} className="w-full h-12 text-base font-bold uppercase tracking-wider" data-testid="register-back-to-login">
            Voltar ao login
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 sm:p-6" data-testid="register-page">
      <Card className="w-full max-w-2xl vv-card p-5 sm:p-8 my-6">
        <div className="flex items-center gap-3 mb-4">
          <img src={BRAND.LOGO} alt="" className="h-14 vv-logo-glow" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[hsl(var(--vv-lime))]">Atleta</div>
            <h1 className="text-2xl font-black leading-none vv-gradient-text">Cadastre-se</h1>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mb-5">
          Preencha seus dados — o cadastro ficará pendente até o administrador aprovar. A turma será definida pelo administrador após a aprovação.
        </p>

        <form onSubmit={submit} className="space-y-4" data-testid="register-form">
          <div>
            <Label className="uppercase text-xs tracking-wider mb-2 block">Foto</Label>
            <PhotoUploader value={form.photo_url} onChange={(v) => setForm({ ...form, photo_url: v })} />
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="uppercase text-xs tracking-wider">Nome *</Label>
              <Input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="register-first-name" />
            </div>
            <div>
              <Label className="uppercase text-xs tracking-wider">Sobrenome</Label>
              <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="register-last-name" />
            </div>
            <div>
              <Label className="uppercase text-xs tracking-wider">Data de nascimento *</Label>
              <Input type="date" required value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} data-testid="register-birth-date" />
            </div>
            <div>
              <Label className="uppercase text-xs tracking-wider">CPF</Label>
              <Input value={form.cpf} onChange={(e) => setForm({ ...form, cpf: e.target.value })} placeholder="000.000.000-00" data-testid="register-cpf" />
            </div>
            <div>
              <Label className="uppercase text-xs tracking-wider">Posição em quadra</Label>
              <Select value={form.position || "__none__"} onValueChange={(v) => setForm({ ...form, position: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="register-position"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">—</SelectItem>
                  {POSITIONS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="uppercase text-xs tracking-wider">Telefone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="register-phone" />
            </div>
            <div>
              <Label className="uppercase text-xs tracking-wider">Responsável</Label>
              <Input value={form.guardian} onChange={(e) => setForm({ ...form, guardian: e.target.value })} placeholder="Nome do responsável" data-testid="register-guardian" />
            </div>
            <div>
              <Label className="uppercase text-xs tracking-wider">Telefone do responsável</Label>
              <Input value={form.guardian_phone} onChange={(e) => setForm({ ...form, guardian_phone: e.target.value })} data-testid="register-guardian-phone" />
            </div>
            <div>
              <Label className="uppercase text-xs tracking-wider">Email *</Label>
              <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="register-email" />
            </div>
            <div>
              <Label className="uppercase text-xs tracking-wider">Senha *</Label>
              <Input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="register-password" />
              <p className="text-[10px] text-muted-foreground mt-1">Mínimo 6 caracteres</p>
            </div>
          </div>

          <Button
            type="submit" disabled={loading}
            data-testid="register-submit-button"
            className="w-full h-11 font-black uppercase tracking-widest bg-gradient-to-r from-[hsl(var(--vv-blue))] to-[hsl(var(--vv-lime))] text-[hsl(218_50%_10%)]"
          >
            {loading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
            {loading ? "Enviando..." : "Cadastrar"}
          </Button>
        </form>

        <Link to="/login" className="mt-5 flex items-center justify-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="register-back-link">
          <ArrowLeft className="size-3" /> Voltar ao login
        </Link>
      </Card>
    </div>
  );
}
